import type { FC, ReactNode } from "react";
import { useEnvironment } from "./EnvironmentContext";
import {
  type FeatureName,
  type FeatureVariant,
  useFeatureManifest,
} from "./FeatureManifestContext";

export interface FeatureFlags {
  FEATURE_ENROLLMENT_CATEGORY_PACKAGE_FILTER: boolean;
  FEATURE_PRESCRIBER: boolean;
  FEATURE_SCHEDULING_CONSOLIDATION: boolean;
  SCHEDULING_CONSOLIDATION_VARIANT: FeatureVariant;
  FEATURE_SCHEDULE_POLLING_STATUS: boolean;
  FEATURE_LABEL: boolean;
  FEATURE_UPDATED_REASON_FOR_VISIT: boolean;
  FEATURE_REASONS_FOR_VISIT_API: boolean;
  FEATURE_UPDATED_REFER_PAGE: boolean;
  FEATURE_PROVIDER_AVAILABILITY_BATCH: boolean;
  ENABLE_PATIENT_PORTAL_REGISTRATION: boolean;
  SCHEDULING_CONSOLIDATION_EXPOSURE?: FeatureExposureMetadata;
}

export interface FeatureExposureMetadata {
  source: "api_assignment" | "percentage_cohort" | "not_exposed";
  configuredPercent: number;
  rolloutBucket: number | null;
  assignedVariant?: FeatureVariant;
}

export const FeatureFlagProvider: FC<{ children: ReactNode }> = ({ children }) => children;

export const useFeatureFlags = () => {
  const {
    FEATURE_PRESCRIBER,
    FEATURE_SCHEDULE_POLLING_STATUS,
    FEATURE_LABEL,
    FEATURE_UPDATED_REASON_FOR_VISIT,
    FEATURE_REASONS_FOR_VISIT_API,
    FEATURE_UPDATED_REFER_PAGE,
    FEATURE_PROVIDER_AVAILABILITY_BATCH,
    ENABLE_PATIENT_PORTAL_REGISTRATION,
  } = useEnvironment();
  const { getVariant } = useFeatureManifest();
  const enrollmentCategoryPackageFilterVariant = getVariant("ENROLLMENT_CATEGORY_PACKAGE_FILTER");
  const isEnrollmentCategoryPackageFilterEnabled =
    enrollmentCategoryPackageFilterVariant === "treatment";
  const schedulingConsolidationVariant = getVariant("SCHEDULING_CONSOLIDATION");
  const isSchedulingConsolidationEnabled = schedulingConsolidationVariant !== "control";
  const schedulingConsolidationExposure: FeatureExposureMetadata = {
    source: isSchedulingConsolidationEnabled ? "api_assignment" : "not_exposed",
    configuredPercent: isSchedulingConsolidationEnabled ? 100 : 0,
    rolloutBucket: null,
    assignedVariant: schedulingConsolidationVariant,
  };

  return {
    FEATURE_ENROLLMENT_CATEGORY_PACKAGE_FILTER: isEnrollmentCategoryPackageFilterEnabled,
    FEATURE_PRESCRIBER,
    FEATURE_SCHEDULING_CONSOLIDATION: isSchedulingConsolidationEnabled,
    SCHEDULING_CONSOLIDATION_VARIANT: schedulingConsolidationVariant,
    FEATURE_SCHEDULE_POLLING_STATUS,
    FEATURE_LABEL,
    FEATURE_UPDATED_REASON_FOR_VISIT,
    FEATURE_REASONS_FOR_VISIT_API,
    FEATURE_UPDATED_REFER_PAGE,
    FEATURE_PROVIDER_AVAILABILITY_BATCH,
    ENABLE_PATIENT_PORTAL_REGISTRATION,
    SCHEDULING_CONSOLIDATION_EXPOSURE: schedulingConsolidationExposure,
  };
};

/** Returns the API-assigned arm for a client-visible feature. */
export const useFeatureVariant = (featureName: FeatureName): FeatureVariant =>
  useFeatureManifest().getVariant(featureName);
